import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import type {
  CloudAccount,
  CloudAccountAuth,
  Diagnostic,
  JsonValue,
  Provider,
  SecretStore,
} from '../api/types';
import { DiagnosticList, Field } from '../components/ui';
import { describedBy } from '../lib/a11y';
import { diagnosticsAt } from '../lib/diagnostics';
import { strings } from '../strings';
import { ACCOUNT_FIELDS, fieldLabel, type AccountField } from './fields';
import { methodsOf, PROVIDERS, providerOrder, providerTile, type MethodBadge } from './providers';

const s = strings.cloudAccounts;

export interface CloudAccountValue {
  name: string;
  provider: string;
  scope: Record<string, string>;
  auth: CloudAccountAuth;
}

function authToText(auth: CloudAccountAuth | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(auth ?? {})) {
    if (key === 'method' || value === null || value === undefined) continue;
    result[key] = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  }
  return result;
}

function checkField(field: AccountField, value: string): string | null {
  if (value === '') return field.required ? strings.common.required : null;
  if (field.pattern && !field.pattern.test(value)) return field.help ?? strings.common.required;
  if (field.kind === 'number') {
    const n = Number(value);
    if (
      !Number.isInteger(n) ||
      (field.min !== undefined && n < field.min) ||
      (field.max !== undefined && n > field.max)
    ) {
      return field.help ?? strings.common.required;
    }
  }
  if (field.kind === 'json') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return s.jsonInvalid;
    } catch {
      return s.jsonInvalid;
    }
  }
  return null;
}

function convert(field: AccountField, value: string): JsonValue {
  if (field.kind === 'number') return Number(value);
  if (field.kind === 'json') return JSON.parse(value) as JsonValue;
  return value;
}

export function MethodBadgeView({ badge }: { badge: MethodBadge | null }) {
  if (!badge) return null;
  const tone =
    badge === 'recommended'
      ? 'tone-success'
      : badge === 'no-secrets'
        ? 'tone-info'
        : badge === 'discouraged'
          ? 'tone-warning'
          : 'tone-neutral';
  return <span className={`badge ${tone}`}>{s.badge[badge]}</span>;
}

export function ProviderTile({ provider, small }: { provider: string; small?: boolean }) {
  return (
    <span aria-hidden className={`provider-tile provider-${provider}${small ? ' is-small' : ''}`}>
      {providerTile(provider)}
    </span>
  );
}

/**
 * Create and edit form of a cloud account (spec 6.6): name, the scope, and the authentication methods as radio
 * cards in the provider's order; read-only users see the values as text. The provider is chosen first when
 * creating and is fixed afterwards.
 */
export function CloudAccountForm({
  formId,
  initial,
  providers,
  secretStores,
  readOnly,
  diagnostics,
  onSubmit,
  onDirtyChange,
}: {
  formId: string;
  initial?: CloudAccount;
  providers: Provider[];
  secretStores: SecretStore[];
  readOnly: boolean;
  diagnostics: Diagnostic[];
  onSubmit: (value: CloudAccountValue) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const ids = useId();
  const [name, setName] = useState(initial?.name ?? '');
  const [provider, setProvider] = useState(initial?.provider ?? 'azure');
  const [scope, setScope] = useState<Record<string, string>>({ ...(initial?.scope ?? {}) });
  const [method, setMethod] = useState(
    initial?.auth.method ?? methodsOf(initial?.provider ?? 'azure')[0]?.key ?? 'ambient',
  );
  const [auth, setAuth] = useState<Record<string, string>>(authToText(initial?.auth));
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [summaryShown, setSummaryShown] = useState(false);

  const dirty = useMemo(() => {
    if (!initial) return true;
    return (
      name !== initial.name ||
      method !== initial.auth.method ||
      JSON.stringify(scope) !== JSON.stringify({ ...initial.scope }) ||
      JSON.stringify(auth) !== JSON.stringify(authToText(initial.auth))
    );
  }, [initial, name, method, scope, auth]);
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  const definition = ACCOUNT_FIELDS[provider] ?? { scope: [], methods: { ambient: [] } };
  const ordered = methodsOf(provider).filter((x) => x.key in definition.methods);
  const methods = ordered.length
    ? ordered
    : Object.keys(definition.methods).map((key) => ({ key, label: key, description: '', badge: null }));
  const methodFields = definition.methods[method] ?? [];
  const id = (suffix: string) => `${ids}-${suffix}`;
  const errorsAt = (pointer: string) => [
    ...(clientErrors[pointer] ? [clientErrors[pointer]] : []),
    ...diagnosticsAt(diagnostics, pointer).map((d) => d.message),
  ];
  const known = ['/name', '/provider', '/scope', '/auth'];
  const general = diagnostics.filter((d) => !known.some((prefix) => d.path?.startsWith(prefix)));
  const providerName = (key: string) =>
    providers.find((p) => p.key === key)?.displayName ?? PROVIDERS[key]?.displayName ?? key;

  // The error summary (spec 6.6.3): every field with a problem, as links that focus the field.
  const problems: { pointer: string; target: string; label: string; message: string }[] = [];
  const addProblems = (pointer: string, target: string, label: string) => {
    for (const message of errorsAt(pointer)) problems.push({ pointer, target, label, message });
  };
  addProblems('/name', id('name'), s.name);
  for (const field of definition.scope)
    addProblems(`/scope/${field.key}`, id(`scope-${field.key}`), fieldLabel(field));
  addProblems('/auth/method', id(`method-${method}`), s.authMethod);
  for (const field of methodFields)
    addProblems(`/auth/${field.key}`, id(`auth-${field.key}`), fieldLabel(field));

  const submit = () => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors['/name'] = strings.common.required;
    for (const field of definition.scope) {
      const problem = checkField(field, scope[field.key] ?? '');
      if (problem) errors[`/scope/${field.key}`] = problem;
    }
    for (const field of methodFields) {
      const problem = checkField(field, auth[field.key] ?? '');
      if (problem) errors[`/auth/${field.key}`] = problem;
    }
    setClientErrors(errors);
    setSummaryShown(true);
    if (Object.keys(errors).length > 0) return;
    const authValue: CloudAccountAuth = { method };
    for (const field of methodFields) {
      const value = auth[field.key] ?? '';
      if (value !== '') authValue[field.key] = convert(field, value);
    }
    onSubmit({
      name: name.trim(),
      provider,
      scope: Object.fromEntries(definition.scope.map((f) => [f.key, scope[f.key] ?? ''])),
      auth: authValue,
    });
  };

  const textValue = (field: AccountField, value: string) => {
    if (field.kind === 'secretStore') return value || strings.settings.defaultStore;
    return value || strings.common.dash;
  };

  const input = (field: AccountField, group: 'scope' | 'auth'): ReactNode => {
    const pointer = `/${group}/${field.key}`;
    const inputId = id(`${group}-${field.key}`);
    const values = group === 'scope' ? scope : auth;
    const setValues = group === 'scope' ? setScope : setAuth;
    const value = values[field.key] ?? '';
    if (readOnly) {
      return (
        <div key={pointer} className="ca-readonly-field">
          <dt>{fieldLabel(field)}</dt>
          <dd className={field.kind === 'json' ? 'font-mono text-xs whitespace-pre-wrap' : 'font-mono'}>
            {textValue(field, value)}
          </dd>
        </div>
      );
    }
    const errors = errorsAt(pointer);
    const helpText = field.kind === 'secretName' ? s.secretFieldHelp : field.help;
    const common = {
      id: inputId,
      'aria-invalid': errors.length ? true : undefined,
      'aria-describedby': describedBy(inputId, !!helpText, errors.length > 0),
    };
    const set = (next: string) => setValues((current) => ({ ...current, [field.key]: next }));
    let control: ReactNode;
    if (field.kind === 'secretStore') {
      control = (
        <select {...common} className="input" value={value} onChange={(e) => set(e.target.value)}>
          <option value="">{strings.settings.defaultStore}</option>
          {secretStores.map((store) => (
            <option key={store.name} value={store.name}>
              {store.name} ({store.kind})
            </option>
          ))}
        </select>
      );
    } else if (field.kind === 'json') {
      control = (
        <textarea
          {...common}
          className="input font-mono"
          rows={8}
          spellCheck={false}
          value={value}
          onChange={(e) => set(e.target.value)}
        />
      );
    } else {
      control = (
        <input
          {...common}
          className="input font-mono"
          type={field.kind === 'number' ? 'number' : 'text'}
          min={field.min}
          max={field.max}
          autoComplete="off"
          value={value}
          onChange={(e) => set(e.target.value)}
        />
      );
    }
    return (
      <Field
        key={pointer}
        label={fieldLabel(field)}
        htmlFor={inputId}
        help={helpText}
        errors={errors}
        required={field.required}
      >
        {control}
      </Field>
    );
  };

  const selectedMethod = methods.find((x) => x.key === method);

  return (
    <form
      id={formId}
      noValidate
      className="ca-form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {summaryShown && problems.length > 0 && (
        <div
          role="alert"
          className="tone-danger mb-3 rounded-md border p-3 text-sm"
          data-testid="error-summary"
        >
          <p className="font-semibold">{s.errorSummary(problems.length)}</p>
          <ul className="mt-1 list-disc pl-5">
            {problems.map((problem, i) => (
              <li key={i}>
                <a
                  className="link"
                  href={`#${problem.target}`}
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById(problem.target)?.focus();
                  }}
                >
                  {problem.label}: {problem.message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {general.length > 0 && (
        <div className="mb-3">
          <DiagnosticList diagnostics={general} />
        </div>
      )}

      {!initial && (
        <fieldset className="mb-4">
          <legend className="label">{s.provider}</legend>
          <div className="ca-provider-choice" role="radiogroup" aria-label={s.provider}>
            {providerOrder(Object.keys(ACCOUNT_FIELDS)).map((key) => (
              <label key={key} className={`ca-provider-option${provider === key ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name={`${ids}-provider`}
                  value={key}
                  checked={provider === key}
                  onChange={() => {
                    setProvider(key);
                    setScope({});
                    setMethod(methodsOf(key)[0]?.key ?? 'ambient');
                    setAuth({});
                  }}
                />
                <ProviderTile provider={key} small />
                <span>{providerName(key)}</span>
              </label>
            ))}
          </div>
          <p className="help">{s.providerFixed}</p>
        </fieldset>
      )}

      <section className="card ca-card mb-3" aria-labelledby={id('general-title')}>
        <h3 id={id('general-title')} className="ca-card-title">
          {s.general}
        </h3>
        {readOnly ? (
          <dl>
            <div className="ca-readonly-field">
              <dt>{s.name}</dt>
              <dd>{name}</dd>
            </div>
          </dl>
        ) : (
          <Field label={s.name} htmlFor={id('name')} errors={errorsAt('/name')} required>
            <input
              id={id('name')}
              className="input"
              value={name}
              aria-invalid={errorsAt('/name').length ? true : undefined}
              aria-describedby={describedBy(id('name'), false, errorsAt('/name').length > 0)}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        )}
      </section>

      <section className="card ca-card mb-3" aria-labelledby={id('scope-title')}>
        <h3 id={id('scope-title')} className="ca-card-title">
          {s.scope} <span className="ca-card-sub">{s.scopeHelp}</span>
        </h3>
        {readOnly ? (
          <dl className="ca-grid">{definition.scope.map((field) => input(field, 'scope'))}</dl>
        ) : (
          <div className="ca-grid">{definition.scope.map((field) => input(field, 'scope'))}</div>
        )}
      </section>

      <section className="card ca-card" aria-labelledby={id('auth-title')}>
        <h3 id={id('auth-title')} className="ca-card-title">
          {s.auth} <span className="ca-card-sub">{s.authHelp}</span>
        </h3>
        {readOnly ? (
          <div className="ca-method is-selected">
            <div className="ca-method-head">
              <span className="font-semibold">{selectedMethod?.label ?? method}</span>
              <MethodBadgeView badge={selectedMethod?.badge ?? null} />
            </div>
            {selectedMethod?.description && <p className="help">{selectedMethod.description}</p>}
            {methodFields.length > 0 && (
              <dl className="mt-2">{methodFields.map((field) => input(field, 'auth'))}</dl>
            )}
          </div>
        ) : (
          <div role="radiogroup" aria-labelledby={id('auth-title')} className="ca-methods">
            {methods.map((entry) => {
              const selected = entry.key === method;
              const descriptionId = id(`method-${entry.key}-description`);
              return (
                <div
                  key={entry.key}
                  className={`ca-method${selected ? ' is-selected' : ''}`}
                  data-method={entry.key}
                >
                  <label className="ca-method-head">
                    <input
                      id={id(`method-${entry.key}`)}
                      type="radio"
                      name={`${ids}-method`}
                      value={entry.key}
                      checked={selected}
                      aria-describedby={descriptionId}
                      onChange={() => {
                        setMethod(entry.key);
                        setAuth({});
                      }}
                    />
                    <span className="font-semibold">{entry.label}</span>
                    <MethodBadgeView badge={entry.badge} />
                  </label>
                  <p id={descriptionId} className="help ca-method-description">
                    {entry.description}
                  </p>
                  {selected && methodFields.length > 0 && (
                    <div className="ca-method-fields">
                      {methodFields.map((field) => input(field, 'auth'))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </form>
  );
}
